import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../context";
import { useFetchData } from "../../../../api/api/FetchData";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DatePickerStyles.css";
import AddExpenseTypeDialog from "./AddExpenseTypeDialog";
import { useTranslation } from "react-i18next";
import useAdd from "../../../../api/useAdd";
import { useNavigate } from "react-router-dom";
import useFetchObjects from "../../../../api/useFetchObjects";

const buttonStyle = {
  textTransform: "capitalize",
  backgroundColor: "#071952",
  margin: "0px 10px",
};

const Expenses = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const { expenseTypeData } = useFetchData();
  const [openExpenseTypeDialog, setOpenExpenseTypeDialog] = useState(false);
  const navigate = useNavigate();

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  // const { data: expenseSourceData } = useFetchObjects(
  //   ["expense-sources"],
  //   "expense-sources",
  //   token
  // );

  const [formData, setFormData] = useState({
    amount: "",
    expenseType: "",
    // expenseSource: "",
    description: "",
    date: new Date(),
    time: getCurrentTime(),
    file: null,
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (event) => {
    const { name, value, files } = event.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, date });
  };

  const validateForm = () => {
    let isValid = true;
    let newErrors = {};

    if (formData.amount && formData.amount <= 0) {
      newErrors.amount = t("finance.validAmountRequired");
      isValid = false;
    }

    if (!formData.amount) {
      newErrors.amount = t("finance.amountRequired");
      isValid = false;
    }
    if (!formData.expenseType) {
      newErrors.expenseType = t("finance.expenseRequired");
      isValid = false;
    }
    if (!formData.date) {
      newErrors.date = t("finance.expenseDateRequired");
      isValid = false;
    }
    if (!formData.time) {
      newErrors.time = t("finance.expenseTimeRequired");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const { handleAdd, resetForm } = useAdd(
    "expenses",
    token,
    t("finance.addSuccess"),
    t("finance.failedAdd")
  );

  useEffect(() => {
    if (resetForm) {
      navigate(-1);
    }
  }, [resetForm, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const data = new FormData();
      data.append("amount", formData.amount);
      data.append("expenseType", formData.expenseType);
      // data.append("expenseSource", formData.expenseSource);
      data.append("date", formData.date.toISOString().split("T")[0]);
      data.append("time", formData.time);
      data.append("description", formData.description);
      if (formData.file) {
        data.append("file", formData.file);
      }
      handleAdd(data);
    }
  };

  return (
    <Grid container maxWidth={"xl"} mt={2} justifyContent={"center"} mb={10}>
      <Grid item xs={12} lg={11.8} component="form" onSubmit={handleSubmit}>
        <Paper sx={{ boxShadow: "0px 0px 0px ", padding: "20px", pb: 10 }}>
          <Grid item xs={12} md={10} margin={"0px auto"}>
            <Grid
              container
              rowGap={2}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Grid item xs={12}>
                {/* Go Back Icon and Title */}
                <Box display="flex" alignItems="center" mb={4}>
                  <Tooltip title={t("finance.goBack")} arrow>
                    <IconButton
                      onClick={() => navigate(-1)}
                      aria-label={t("finance.goBack")}
                      sx={{ mr: 2 }}
                    >
                      <ArrowBackIcon size="large" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="h4">{t("finance.expenses")}</Typography>
              </Grid>

              <Grid item xs={12} md={3.5}>
                <FormLabel>{t("finance.expenseAmount")}</FormLabel>
                <TextField
                  fullWidth
                  type="number"
                  name="amount"
                  size="small"
                  inputProps={{ step: "0.01", min: "0" }}
                  value={formData.amount}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid container gap={2} justifyContent={"space-between"} mt={2}>
                <Grid item xs={12} md={3.5}>
                  <FormControl
                    size="small"
                    fullWidth
                    error={!!errors.expenseType}
                  >
                    <FormLabel>{t("finance.expenseType")}</FormLabel>
                    <Select
                      labelId="expense-type-label"
                      id="expense-type"
                      name="expenseType"
                      value={formData.expenseType}
                      onChange={handleInputChange}
                    >
                      {(Array.isArray(expenseTypeData)
                        ? expenseTypeData
                        : []
                      ).map((item, index) => (
                        <MenuItem value={item.id} key={index}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.expenseType && (
                      <FormHelperText>{errors.expenseType}</FormHelperText>
                    )}
                  </FormControl>

                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setOpenExpenseTypeDialog(true)}
                    sx={{ display: "block", mt: 1 }}
                  >
                    {t("finance.addNewExpenseType")}
                  </Button>
                </Grid>

                {/* <Grid item xs={12} md={3.5}>
                  <FormControl size="small" fullWidth>
                    <FormLabel>{t("finance.expenseSource")}</FormLabel>
                    <Select
                      labelId="expense-source-label"
                      id="expense-source"
                      name="expenseSource"
                      value={formData.expenseSource}
                      onChange={handleInputChange}
                    >
                      {(Array.isArray(expenseSourceData)
                        ? expenseSourceData
                        : []
                      ).map((item, index) => (
                        <MenuItem value={item.id} key={index}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid> */}

                <Grid item xs={12} md={3.5}>
                  <FormControl fullWidth size="small">
                    <FormLabel>{t("finance.expenseDate")}</FormLabel>
                    <DatePicker
                      selected={formData.date}
                      onChange={handleDateChange}
                      dateFormat="yyyy-MM-dd"
                      className="form-control date-picker-input"
                    />
                    {errors.date && (
                      <FormHelperText>{errors.date}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3.5}>
                  <FormControl fullWidth size="small">
                    <FormLabel>{t("finance.expenseTime")}</FormLabel>
                    <TextField
                      fullWidth
                      type="time"
                      name="time"
                      size="small"
                      value={formData.time}
                      error={!!errors.time}
                      helperText={errors.time}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Grid item xs={12} mt={3}>
                <FormLabel>{t("finance.description")}</FormLabel>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  name="description"
                  size="small"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12} mt={3}>
                <FormLabel>{t("finance.uploadFile")}</FormLabel>
                <TextField
                  fullWidth
                  type="file"
                  name="file"
                  size="small"
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid container mt={2} justifyContent={"end"}>
                <Button variant="contained" type="submit" style={buttonStyle}>
                  {t("finance.saveNew")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <AddExpenseTypeDialog
        open={openExpenseTypeDialog}
        onClose={() => setOpenExpenseTypeDialog(false)}
        token={token}
      />
    </Grid>
  );
};

export default Expenses;
